import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { FormTemplate } from "../types/forms";
import { getFormByUuid } from "../services/consultationform";
import UserPreview from "./forms/[id]/builder/UserPreview";

import { FormSection } from "../types/forms";

const mapSectionType = (section: FormSection): FormSection => {
  if (section.type) return section;

  const templateIdMap: Record<number, FormSection["type"]> = {
    1: "contact",
    2: "medical",
    3: "marketing",
    4: "question",
    5: "heading",
    6: "text",
    7: "policy",
    8: "disclaimer",
  };

  return {
    ...section,
    type: section.form_template_id ? templateIdMap[section.form_template_id] || null : null
  };
};

const deriveSectionTitle = (s: FormSection): string | null => {
  let sectionTitle = s.title ?? null;
  if ((s.type === "text" || s.type === "heading") && !sectionTitle?.trim() && (s.fields?.length ?? 0) > 0) {
    const first = s.fields![0];
    if (typeof first.options === "string" && first.options.trim()) sectionTitle = first.options.trim();
    else if (first.label?.trim()) sectionTitle = first.label.trim();
    else if (first.options && typeof first.options === "object" && first.options !== null && "content" in first.options) {
      const opts = first.options as { content?: string };
      if (opts.content?.trim()) sectionTitle = opts.content.trim();
    }
  }
  return sectionTitle;
};

export default function PublicFormPage() {
  const { templateId } = useParams<{ templateId: string }>();
  const [template, setTemplate] = useState<FormTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!templateId) return;

    const fetchTemplate = async () => {
      try {
        setLoading(true);
        const res = await getFormByUuid(templateId);
        if (res?.form) {
          const form = res.form;
          const mappedForm: FormTemplate = {
            ...form,
            id: String(form.id ?? ""),
            uuid: form.uuid ?? "",
            sections: (form.sections || [])
              .map((s: FormSection) => {
                const mapped = mapSectionType(s);
                return { ...mapped, title: deriveSectionTitle(mapped) ?? mapped.title };
              })
              .sort((a: FormSection, b: FormSection) => (a.order || 0) - (b.order || 0))
          };
          setTemplate(mappedForm);
        } else {
          setError("Template not found");
        }
      } catch (err: any) {
        setError(err.message || "Failed to load template");
      } finally {
        setLoading(false);
      }
    };

    fetchTemplate();
  }, [templateId]);

  if (!templateId) {
    return (
      <div className="p-8 text-center text-red-500">
        Template ID is missing in the URL.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen text-gray-500">
        Loading public form...
      </div>
    );
  }

  if (error || !template) {
    return (
      <div className="p-8 text-center text-red-500">
        {error || "Failed to load template."}
      </div>
    );
  }

  return (
    <div className="h-screen overflow-y-auto bg-gray-100 py-12">
      <UserPreview template={template} showSubmit={false} />
    </div>
  );
}
